import { API_BASE } from "../utils/api";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../components/Toast";

const CATEGORY_LABELS = {
  tax_return: "Tax Return",
  invoice: "Invoice",
  payslip: "Payslip",
  bank_statement: "Bank Statement",
  expense: "Expense",
  contract: "Contract",
  other: "Other",
};

const CATEGORY_PILL = {
  tax_return:    "bg-yellow-50    dark:bg-luxury-elevated text-yellow-700  dark:text-luxury-gold   border border-yellow-200  dark:border-luxury-gold/30",
  invoice:       "bg-amber-50     dark:bg-luxury-elevated text-amber-700   dark:text-yellow-400    border border-amber-200   dark:border-yellow-400/30",
  payslip:       "bg-emerald-50   dark:bg-luxury-elevated text-emerald-700 dark:text-luxury-cta    border border-emerald-200 dark:border-luxury-cta/30",
  bank_statement:"bg-purple-50    dark:bg-luxury-elevated text-purple-700  dark:text-purple-400    border border-purple-200  dark:border-purple-400/30",
  expense:       "bg-red-50       dark:bg-luxury-elevated text-red-700     dark:text-red-400       border border-red-200     dark:border-red-400/30",
  contract:      "bg-blue-50      dark:bg-luxury-elevated text-blue-700    dark:text-blue-400      border border-blue-200    dark:border-blue-400/30",
  other:         "bg-luxury-elevated text-luxury-secondary border border-luxury-border",
};

function formatFileSize(bytes) {
  if (!bytes) return "—";
  const k = 1024;
  const sizes = ["B", "KB", "MB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
}

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-ZA", {
    day: "2-digit", month: "short", year: "numeric",
  });
}

function FileIconBox({ fileType }) {
  const getStyle = () => {
    if (!fileType) return "bg-luxury-elevated text-luxury-muted";
    if (fileType.includes("pdf"))    return "bg-red-100     dark:bg-red-900/30    text-red-600    dark:text-red-400";
    if (fileType.includes("image"))  return "bg-purple-100  dark:bg-purple-900/30 text-purple-600 dark:text-purple-400";
    if (fileType.includes("word") || fileType.includes("document")) return "bg-yellow-50 dark:bg-luxury-elevated text-yellow-700 dark:text-luxury-gold";
    if (fileType.includes("excel") || fileType.includes("sheet")) return "bg-emerald-100 dark:bg-green-900/30 text-emerald-600 dark:text-green-400";
    return "bg-luxury-elevated text-luxury-muted";
  };

  const getIcon = () => {
    if (fileType?.includes("pdf")) return (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    );
    return (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
      </svg>
    );
  };

  return (
    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${getStyle()}`}>
      {getIcon()}
    </div>
  );
}

export default function Dashboard() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [documents, setDocuments]   = useState([]);
  const [stats, setStats]           = useState(null);
  const [loading, setLoading]       = useState(true);
  const [searchQuery, setSearchQuery]       = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [deletingId, setDeletingId]         = useState(null);
  const [sarsDismissed, setSarsDismissed]   = useState(
    () => sessionStorage.getItem("sars-banner-dismissed") === "1"
  );
  const [showReport, setShowReport]         = useState(false);
  const [smartReminder, setSmartReminder]   = useState(null);

  // ── Smart Deadline Reminder ──────────────────────────────────────
  useEffect(() => {
    const DEADLINES = [
      { date: "2025-07-01", title: "SARS Filing Season Opens",         days: [30, 7, 1] },
      { date: "2025-10-22", title: "Filing Closes — Branch / Post",    days: [30, 7, 1] },
      { date: "2025-11-20", title: "Filing Closes — eFiling",          days: [30, 7, 1] },
      { date: "2025-11-28", title: "Provisional Tax 2nd Payment Due",  days: [30, 7, 1] },
      { date: "2026-01-31", title: "Provisional Taxpayer Filing Deadline", days: [30, 7, 1] },
    ];
    const today = new Date(); today.setHours(0,0,0,0);
    const dismissed = JSON.parse(localStorage.getItem("tvg-dismissed-reminders") || "[]");

    for (const d of DEADLINES) {
      const target = new Date(d.date);
      const diff = Math.ceil((target - today) / (1000*60*60*24));
      for (const threshold of d.days) {
        const key = `${d.date}-${threshold}`;
        if (diff === threshold && !dismissed.includes(key)) {
          setSmartReminder({ ...d, daysLeft: diff, key });

          // Browser notification
          if (Notification.permission === "granted") {
            new Notification("TaxVault — SARS Reminder 🔔", {
              body: `${d.title} — ${diff} day${diff !== 1 ? "s" : ""} left!`,
              icon: "/favicon.ico",
            });
          } else if (Notification.permission !== "denied") {
            Notification.requestPermission().then(p => {
              if (p === "granted") new Notification("TaxVault — SARS Reminder 🔔", {
                body: `${d.title} — ${diff} day${diff !== 1 ? "s" : ""} left!`,
              });
            });
          }
          break;
        }
      }
      if (smartReminder) break;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismissReminder = (key) => {
    const prev = JSON.parse(localStorage.getItem("tvg-dismissed-reminders") || "[]");
    localStorage.setItem("tvg-dismissed-reminders", JSON.stringify([...prev, key]));
    setSmartReminder(null);
  };

  const fetchData = async () => {
    try {
      const [docsRes, statsRes] = await Promise.all([
        fetch(`${API_BASE}/api/documents/`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_BASE}/api/documents/stats/summary`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (docsRes.ok)   { const d = await docsRes.json();   setDocuments(d.documents || []); }
      if (statsRes.ok)  { const s = await statsRes.json();  setStats(s); }
    } catch (err) {
      console.error("Failed to fetch data:", err);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, [token]);

  const handleDelete = async (docId) => {
    if (!window.confirm("Delete this document?")) return;
    setDeletingId(docId);
    try {
      const res = await fetch(`${API_BASE}/api/documents/${docId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setDocuments((prev) => prev.filter((d) => d.id !== docId));
        setStats((prev) => prev ? { ...prev, total_documents: Math.max(0, prev.total_documents - 1) } : prev);
        addToast("Document deleted successfully", "success");
      }
    } catch (err) {
      addToast("Failed to delete document", "error");
    } finally {
      setDeletingId(null);
    }
  };

  const dismissSarsBanner = () => {
    sessionStorage.setItem("sars-banner-dismissed", "1");
    setSarsDismissed(true);
  };

  const filteredDocs = documents.filter((doc) => {
    const matchSearch = !searchQuery ||
      doc.original_filename?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doc.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchCategory = !filterCategory || doc.category === filterCategory;
    return matchSearch && matchCategory;
  });

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 18) return "Good afternoon";
    return "Good evening";
  };

  const firstName  = user?.full_name?.split(" ")[0] || "there";
  const totalDocs  = stats?.total_documents ?? 0;

  // Days until 1 July
  const today   = new Date();
  const july1   = new Date(today.getFullYear(), 6, 1);
  if (july1 < today) july1.setFullYear(today.getFullYear() + 1);
  const daysToFiling = Math.ceil((july1 - today) / (1000 * 60 * 60 * 24));

  const statCards = [
    {
      label: "Total Documents",
      value: loading ? "—" : totalDocs,
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
        </svg>
      ),
      iconStyle:    "bg-yellow-50 dark:bg-luxury-elevated text-yellow-600 dark:text-luxury-gold",
      valueStyle:   "text-luxury-gold",
      accentBorder: "border-t-luxury-gold",
    },
    {
      label: "Tax Returns",
      value: loading ? "—" : (stats?.by_category?.tax_return ?? 0),
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" />
        </svg>
      ),
      iconStyle:    "bg-emerald-50 dark:bg-luxury-cta/10 text-emerald-600 dark:text-luxury-cta",
      valueStyle:   "text-luxury-cta",
      accentBorder: "border-t-luxury-cta",
    },
    {
      label: "Invoices",
      value: loading ? "—" : (stats?.by_category?.invoice ?? 0),
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
      ),
      iconStyle:    "bg-amber-100 dark:bg-yellow-900/30 text-amber-700 dark:text-yellow-400",
      valueStyle:   "text-amber-700 dark:text-yellow-400",
      accentBorder: "border-t-amber-400",
    },
    {
      label: "Bank Statements",
      value: loading ? "—" : (stats?.by_category?.bank_statement ?? 0),
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
        </svg>
      ),
      iconStyle:    "bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400",
      valueStyle:   "text-purple-700 dark:text-purple-400",
      accentBorder: "border-t-purple-400",
    },
  ];


  return (
    <div className="min-h-screen bg-luxury-bg p-6 lg:p-8">

      {/* ── Welcome Banner ──────────────────────────────────────────── */}
      <div className="relative overflow-hidden rounded-2xl mb-6 bg-luxury-surface border border-luxury-border p-6 lg:p-8">
        {/* Subtle gold circle decoration */}
        <div className="absolute top-0 right-0 w-48 h-48 bg-luxury-gold/[0.04] rounded-full -translate-y-1/3 translate-x-1/3 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-luxury-cta/[0.03] rounded-full translate-y-1/2 -translate-x-1/3 pointer-events-none" />

        <div className="relative">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <p className="text-luxury-muted text-xs font-medium mb-1 uppercase tracking-wider">
                {new Date().toLocaleDateString("en-ZA", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
              </p>
              <h1 className="text-2xl lg:text-3xl font-bold text-luxury-text">
                {greeting()}, <span className="text-luxury-gold">{firstName}</span>
              </h1>
              <p className="text-luxury-secondary mt-1 text-sm">
                {loading
                  ? "Loading your vault..."
                  : totalDocs === 0
                    ? "Your vault is ready — upload your first document."
                    : `Your vault is secure · ${totalDocs} document${totalDocs !== 1 ? "s" : ""} stored · South Africa`}
              </p>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              {totalDocs > 0 && (
                <button
                  onClick={() => setShowReport(true)}
                  className="hidden sm:inline-flex items-center gap-2 bg-luxury-elevated hover:bg-luxury-border border border-luxury-border text-luxury-text font-semibold px-4 py-2.5 rounded-xl transition-colors text-sm active:scale-95"
                >
                  <svg className="w-4 h-4 text-luxury-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Tax Report
                </button>
              )}
              <button
                onClick={() => navigate("/upload")}
                className="inline-flex items-center gap-2 bg-luxury-cta hover:bg-luxury-cta-hover text-white font-semibold px-5 py-2.5 rounded-xl transition-colors text-sm active:scale-95"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Upload Document
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── SARS Filing Season Alert ─────────────────────────────────── */}
      {!sarsDismissed && (
        <div className="rounded-xl border border-luxury-gold/40 bg-yellow-50 dark:bg-luxury-gold/5 px-4 py-3 mb-6 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-yellow-100 dark:bg-luxury-elevated flex items-center justify-center flex-shrink-0">
              <svg className="w-4 h-4 text-luxury-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <div className="min-w-0">
              <span className="text-sm font-semibold text-luxury-text">SARS Filing Season 2025</span>
              <span className="hidden sm:inline text-xs text-luxury-secondary ml-2">
                Opens 1 July 2025 — ensure all your ITR12 documents are ready to submit.
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-xs font-bold text-luxury-gold border border-luxury-gold/40 rounded-lg px-2.5 py-1 bg-yellow-50 dark:bg-luxury-elevated whitespace-nowrap">
              {daysToFiling}d left
            </span>
            <button
              onClick={dismissSarsBanner}
              className="p-1 rounded-lg text-luxury-muted hover:text-luxury-text hover:bg-luxury-elevated transition-colors"
              title="Dismiss"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* ── Smart Deadline Reminder ──────────────────────────────────── */}
      {smartReminder && (
        <div className={`rounded-xl border px-5 py-4 mb-6 flex items-center gap-4
          ${smartReminder.daysLeft <= 7
            ? "border-red-200 dark:border-red-500/40 bg-red-50 dark:bg-red-900/10"
            : "border-amber-200 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-900/10"}`}>
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0
            ${smartReminder.daysLeft <= 7 ? "bg-red-100 dark:bg-red-900/30" : "bg-amber-100 dark:bg-amber-900/30"}`}>
            <svg className={`w-5 h-5 ${smartReminder.daysLeft <= 7 ? "text-red-600 dark:text-red-400" : "text-amber-600 dark:text-amber-400"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-luxury-text">🔔 {smartReminder.title}</p>
            <p className="text-xs text-luxury-secondary">
              {smartReminder.daysLeft === 1 ? "Tomorrow!" : `${smartReminder.daysLeft} days left`} — Make sure your documents are uploaded and ready.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className={`text-xs font-bold px-2.5 py-1 rounded-lg border ${smartReminder.daysLeft <= 7 ? "text-red-700 dark:text-red-400 border-red-200 dark:border-red-500/40 bg-red-50 dark:bg-red-900/20" : "text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-900/20"}`}>
              {smartReminder.daysLeft}d left
            </span>
            <button onClick={() => dismissReminder(smartReminder.key)} className="p-1 rounded-lg text-luxury-muted hover:text-luxury-text hover:bg-luxury-elevated transition-colors" title="Dismiss">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* ── New User Onboarding (0 documents) ──────────────────────── */}
      {!loading && totalDocs === 0 && (
        <div className="rounded-2xl bg-luxury-surface border border-luxury-border p-8 mb-6 text-center">
          <div className="w-16 h-16 rounded-2xl bg-luxury-elevated border border-luxury-gold/30 flex items-center justify-center mx-auto mb-5">
            <svg className="w-8 h-8 text-luxury-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-luxury-text mb-2">Your Secure Tax Document Vault</h2>
          <p className="text-luxury-secondary text-sm max-w-md mx-auto mb-6">
            Upload your tax documents and TaxVault will keep them safe, organised, and SARS-ready. Start by uploading your first document.
          </p>
          <button
            onClick={() => navigate("/upload")}
            className="inline-flex items-center gap-2 bg-luxury-cta hover:bg-luxury-cta-hover text-white font-bold px-8 py-3 rounded-xl transition-colors text-sm active:scale-95 shadow-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            Upload First Document
          </button>
          <div className="grid grid-cols-3 gap-4 mt-8 pt-6 border-t border-luxury-border">
            {[
              { icon: "🔒", title: "Bank-Level Security", desc: "AES-256 encrypted storage" },
              { icon: "📋", title: "SARS Compliant", desc: "Built for South African tax requirements" },
              { icon: "⚡", title: "Instant Access", desc: "Retrieve any document in seconds" },
            ].map(f => (
              <div key={f.title} className="text-center">
                <div className="text-2xl mb-2">{f.icon}</div>
                <p className="text-xs font-semibold text-luxury-text">{f.title}</p>
                <p className="text-xs text-luxury-muted mt-0.5">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Stat Cards (only when user has documents) ────────────────── */}
      {totalDocs > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {statCards.map((card) => (
            <div
              key={card.label}
              className={`rounded-2xl p-5 border border-luxury-border border-t-2 bg-luxury-surface
                transition-all hover:scale-[1.02] hover:shadow-sm cursor-default ${card.accentBorder}`}
            >
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 ${card.iconStyle}`}>
                {card.icon}
              </div>
              <div className={`text-2xl lg:text-3xl font-bold mb-1 ${card.valueStyle}`}>
                {card.value}
              </div>
              <div className="text-xs font-medium text-luxury-secondary uppercase tracking-wide">{card.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* ── Documents Table ──────────────────────────────────────────── */}
      <div className="rounded-2xl border border-luxury-border bg-luxury-surface overflow-hidden">

        {/* Header */}
        <div className="px-6 py-4 border-b border-luxury-border">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-base font-bold text-luxury-text">My Documents</h2>
                {filteredDocs.length > 0 && (
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-luxury-elevated text-luxury-secondary border border-luxury-border">
                    {filteredDocs.length}
                  </span>
                )}
              </div>
              <p className="text-xs mt-0.5 text-luxury-muted">All your tax documents in one place</p>
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              {/* Search */}
              <div className="relative flex-1 sm:flex-none">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-luxury-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Search documents..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 pr-4 py-2 border border-luxury-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-luxury-gold/30 w-full sm:w-48 bg-luxury-elevated text-luxury-text placeholder-luxury-muted"
                />
              </div>
              {/* Category filter */}
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="border border-luxury-border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-luxury-gold/30 bg-luxury-elevated text-luxury-secondary"
              >
                <option value="">All</option>
                {Object.entries(CATEGORY_LABELS).map(([val, label]) => (
                  <option key={val} value={val}>{label}</option>
                ))}
              </select>
              {/* View all link */}
              <button
                onClick={() => navigate("/documents")}
                className="hidden sm:inline-flex items-center gap-1 text-xs font-medium text-luxury-gold hover:text-luxury-gold/80 transition-colors whitespace-nowrap"
              >
                View all
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Table Column Headers */}
        {filteredDocs.length > 0 && (
          <div className="hidden md:grid grid-cols-12 px-6 py-3 text-xs font-semibold uppercase tracking-wider bg-luxury-elevated text-luxury-muted border-b border-luxury-border">
            <div className="col-span-5">Document</div>
            <div className="col-span-2">Category</div>
            <div className="col-span-2">Tax Year</div>
            <div className="col-span-2">Size / Date</div>
            <div className="col-span-1"></div>
          </div>
        )}

        {/* Rows */}
        {loading ? (
          <div className="p-6 space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-14 rounded-xl animate-pulse bg-luxury-elevated" />
            ))}
          </div>
        ) : filteredDocs.length === 0 ? (
          <div className="p-16 text-center">
            <div className="w-16 h-16 rounded-2xl mx-auto flex items-center justify-center mb-4 bg-luxury-elevated">
              <svg className="w-8 h-8 text-luxury-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
              </svg>
            </div>
            <h3 className="text-base font-semibold mb-1 text-luxury-text">
              {searchQuery || filterCategory ? "No documents match your filter" : "No documents yet"}
            </h3>
            <p className="text-sm mb-6 text-luxury-secondary">
              {searchQuery || filterCategory
                ? "Try clearing your search or filter."
                : "Upload your first tax document to get started."}
            </p>
            {!searchQuery && !filterCategory && (
              <button
                onClick={() => navigate("/upload")}
                className="px-6 py-2.5 bg-luxury-cta text-white rounded-xl font-semibold hover:bg-luxury-cta-hover transition active:scale-95 text-sm"
              >
                Upload First Document
              </button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-luxury-border">
            {filteredDocs.map((doc) => (
              <div
                key={doc.id}
                className="grid grid-cols-1 md:grid-cols-12 items-center gap-3 px-6 py-4 transition group hover:bg-luxury-elevated"
              >
                {/* File name + icon */}
                <div className="col-span-5 flex items-center gap-3 min-w-0">
                  <FileIconBox fileType={doc.file_type} />
                  <div className="min-w-0">
                    <p className="font-semibold text-sm truncate text-luxury-text">{doc.original_filename}</p>
                    {doc.description && (
                      <p className="text-xs truncate mt-0.5 text-luxury-secondary">{doc.description}</p>
                    )}
                  </div>
                </div>

                {/* Category */}
                <div className="col-span-2">
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${CATEGORY_PILL[doc.category] || "bg-luxury-elevated text-luxury-secondary border border-luxury-border"}`}>
                    {CATEGORY_LABELS[doc.category] || doc.category}
                  </span>
                </div>

                {/* Tax Year */}
                <div className="col-span-2">
                  {doc.tax_year ? (
                    <span className="text-xs font-semibold px-2.5 py-1 rounded-lg bg-luxury-elevated border border-luxury-gold/20 text-luxury-gold dark:text-luxury-gold text-yellow-700">
                      {doc.tax_year}
                    </span>
                  ) : (
                    <span className="text-sm text-luxury-muted">—</span>
                  )}
                </div>

                {/* Size + Date */}
                <div className="col-span-2">
                  <p className="text-sm text-luxury-secondary">{formatFileSize(doc.file_size)}</p>
                  <p className="text-xs mt-0.5 text-luxury-muted">{formatDate(doc.created_at)}</p>
                </div>

                {/* Delete */}
                <div className="col-span-1 flex justify-end">
                  <button
                    onClick={() => handleDelete(doc.id)}
                    disabled={deletingId === doc.id}
                    className="p-2 rounded-lg transition opacity-0 group-hover:opacity-100 text-luxury-muted hover:text-red-600 dark:hover:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/20"
                    title="Delete document"
                  >
                    {deletingId === doc.id ? (
                      <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Footer — view all */}
        {filteredDocs.length > 0 && (
          <div className="px-6 py-3 border-t border-luxury-border bg-luxury-elevated">
            <button
              onClick={() => navigate("/documents")}
              className="text-xs font-medium text-luxury-gold hover:text-luxury-gold/80 transition-colors flex items-center gap-1"
            >
              View all documents in full library
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* ── Smart Tax Summary Report Modal ──────────────────────── */}
      {showReport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="w-full max-w-lg rounded-2xl bg-luxury-surface border border-luxury-border shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-luxury-border bg-luxury-elevated">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-yellow-50 dark:bg-luxury-elevated border border-luxury-gold/30 flex items-center justify-center">
                  <svg className="w-5 h-5 text-luxury-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-bold text-luxury-text text-sm">Smart Tax Summary Report</h3>
                  <p className="text-xs text-luxury-muted">Generated from your TaxVault documents</p>
                </div>
              </div>
              <button onClick={() => setShowReport(false)} className="p-1.5 rounded-lg text-luxury-muted hover:text-luxury-text hover:bg-luxury-bg transition-colors">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Report Body */}
            <div className="px-6 py-5 space-y-4 max-h-[65vh] overflow-y-auto">
              <div className="text-center pb-3 border-b border-luxury-border">
                <p className="text-xs font-semibold uppercase tracking-wider text-luxury-gold mb-1">TaxVault Global</p>
                <h2 className="text-xl font-bold text-luxury-text">Tax Document Summary</h2>
                <p className="text-xs text-luxury-muted mt-1">
                  {user?.full_name || user?.email} &nbsp;·&nbsp;
                  {new Date().toLocaleDateString("en-ZA", { day: "numeric", month: "long", year: "numeric" })}
                </p>
              </div>

              {/* Overview */}
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: "Total Documents", value: stats?.total_documents ?? 0, color: "text-luxury-gold" },
                  { label: "Tax Years", value: [...new Set(documents.map(d => d.tax_year).filter(Boolean))].length || 0, color: "text-luxury-cta" },
                  { label: "Total Size", value: (() => { const b = documents.reduce((a,d)=>a+(d.file_size||0),0); if(b<1024) return b+"B"; if(b<1048576) return (b/1024).toFixed(1)+"KB"; return (b/1048576).toFixed(1)+"MB"; })(), color: "text-purple-600 dark:text-purple-400" },
                ].map(s => (
                  <div key={s.label} className="text-center p-3 rounded-xl bg-luxury-elevated border border-luxury-border">
                    <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
                    <div className="text-xs text-luxury-secondary mt-0.5">{s.label}</div>
                  </div>
                ))}
              </div>

              {/* By Category */}
              <div>
                <h4 className="text-xs font-bold uppercase tracking-wider text-luxury-muted mb-2">Documents by Category</h4>
                <div className="space-y-1">
                  {Object.entries(CATEGORY_LABELS).map(([key, label]) => {
                    const count = documents.filter(d => d.category === key).length;
                    if (!count) return null;
                    return (
                      <div key={key} className="flex items-center justify-between py-2 border-b border-luxury-border last:border-0">
                        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${CATEGORY_PILL[key]}`}>{label}</span>
                        <span className="text-sm font-bold text-luxury-text">{count}</span>
                      </div>
                    );
                  })}
                  {!documents.length && <p className="text-xs text-luxury-muted text-center py-3">No documents uploaded yet.</p>}
                </div>
              </div>

              {/* Tax Years */}
              {(() => {
                const years = [...new Set(documents.map(d=>d.tax_year).filter(Boolean))].sort().reverse();
                if (!years.length) return null;
                return (
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-wider text-luxury-muted mb-2">By Tax Year</h4>
                    {years.map(y => (
                      <div key={y} className="flex items-center justify-between py-2 border-b border-luxury-border last:border-0">
                        <span className="text-xs font-semibold px-2.5 py-1 rounded-lg bg-luxury-elevated border border-luxury-gold/20 text-luxury-gold">{y}</span>
                        <span className="text-sm font-bold text-luxury-text">{documents.filter(d=>d.tax_year===y).length} docs</span>
                      </div>
                    ))}
                  </div>
                );
              })()}

              {/* Readiness */}
              <div className="rounded-xl border border-luxury-border bg-luxury-elevated p-4">
                <h4 className="text-xs font-bold uppercase tracking-wider text-luxury-muted mb-3">Filing Readiness</h4>
                {[
                  { label: "Tax Returns uploaded", key: "tax_return" },
                  { label: "Bank Statements uploaded", key: "bank_statement" },
                  { label: "Invoices / Expenses uploaded", key: "invoice" },
                ].map(({ label, key }) => {
                  const has = documents.some(d => d.category === key);
                  return (
                    <div key={key} className="flex items-center justify-between mb-2 last:mb-0">
                      <span className="text-xs text-luxury-secondary">{label}</span>
                      <span className={`text-xs font-bold ${has ? "text-luxury-cta" : "text-amber-600 dark:text-amber-400"}`}>
                        {has ? "✅ Ready" : "⚠️ Missing"}
                      </span>
                    </div>
                  );
                })}
              </div>

              <p className="text-xs text-luxury-muted text-center">Generated by TaxVault · For personal reference only</p>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-luxury-border bg-luxury-elevated flex gap-3">
              <button
                onClick={() => window.print()}
                className="flex-1 py-2.5 rounded-xl bg-luxury-cta hover:bg-luxury-cta-hover text-white text-sm font-semibold transition-colors flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
                Print / Save as PDF
              </button>
              <button onClick={() => setShowReport(false)} className="px-5 py-2.5 rounded-xl bg-luxury-bg border border-luxury-border text-luxury-secondary text-sm font-semibold hover:bg-luxury-elevated transition-colors">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
